import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  GitBranch,
  Plus,
  Edit2,
  Trash2,
  Power,
  PowerOff,
  Users,
  User,
  ArrowRight,
  AlertTriangle,
  CheckCircle,
  Settings,
  Lock,
  Unlock,
  Briefcase,
  Building2,
  Activity,
  CreditCard,
  UserCircle,
  Clock,
  Eye,
  BarChart3,
  History,
  FileText,
  Copy,
  TrendingUp,
  Shield,
  RefreshCw,
  Search,
  Filter,
  ChevronRight,
  MoreVertical,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Iconos por módulo
const MODULO_ICONS = {
  'GENERAL': GitBranch,
  'CASOS': Briefcase,
  'EMPRESAS': Building2,
  'ONBOARDING': Building2,
  'ALERTAS': AlertTriangle,
  'MOVIMIENTOS': CreditCard,
  'CLIENTES': UserCircle,
  'MONITORES': Activity
};

const MODULO_COLORS = {
  'GENERAL': 'text-slate-600',
  'CASOS': 'text-blue-600',
  'EMPRESAS': 'text-purple-600',
  'ONBOARDING': 'text-teal-600',
  'ALERTAS': 'text-amber-600',
  'MOVIMIENTOS': 'text-green-600',
  'CLIENTES': 'text-cyan-600',
  'MONITORES': 'text-indigo-600'
};

// Roles disponibles en el sistema
const ROLES_DISPONIBLES = [
  { id: 'ADMINISTRADOR', nombre: 'Administrador' },
  { id: 'SUPERVISOR', nombre: 'Supervisor' },
  { id: 'ANALISTA', nombre: 'Analista' },
  { id: 'COMPLIANCE_OFFICER', nombre: 'Compliance Officer' },
  { id: 'AUDITOR', nombre: 'Auditor' }
];

// Componente para seleccionar aprobadores
const AprobadoresSelectorInline = ({ nivel, aprobadores, onAdd, onRemove, users, groups, getLabel }) => {
  const [tipoAprobador, setTipoAprobador] = useState('ROL');
  const [valorAprobador, setValorAprobador] = useState('');

  const handleAdd = () => {
    if (valorAprobador) {
      onAdd(nivel, tipoAprobador, valorAprobador);
      setValorAprobador('');
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Select value={tipoAprobador} onValueChange={setTipoAprobador}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ROL">Por Rol</SelectItem>
            <SelectItem value="GRUPO">Por Grupo</SelectItem>
            <SelectItem value="USUARIO">Usuario Específico</SelectItem>
          </SelectContent>
        </Select>

        {tipoAprobador === 'ROL' && (
          <Select value={valorAprobador} onValueChange={setValorAprobador}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Seleccionar rol..." />
            </SelectTrigger>
            <SelectContent>
              {ROLES_DISPONIBLES.map(rol => (
                <SelectItem key={rol.id} value={rol.id}>{rol.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {tipoAprobador === 'GRUPO' && (
          <Select value={valorAprobador} onValueChange={setValorAprobador}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Seleccionar grupo..." />
            </SelectTrigger>
            <SelectContent>
              {groups.map(g => (
                <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {tipoAprobador === 'USUARIO' && (
          <Select value={valorAprobador} onValueChange={setValorAprobador}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Seleccionar usuario..." />
            </SelectTrigger>
            <SelectContent>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.nombre} ({u.email})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Button 
          type="button" 
          size="sm" 
          variant="outline" 
          onClick={handleAdd}
          disabled={!valorAprobador}
        >
          <Plus size={14} />
        </Button>
      </div>

      {aprobadores.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {aprobadores.map((a, idx) => (
            <Badge 
              key={idx} 
              variant="secondary" 
              className="flex items-center gap-1 px-2 py-1"
            >
              {a.tipo === 'ROL' && <Shield size={12} className="text-blue-600" />}
              {a.tipo === 'GRUPO' && <Users size={12} className="text-purple-600" />}
              {a.tipo === 'USUARIO' && <User size={12} className="text-green-600" />}
              <span className="text-xs">{getLabel(a)}</span>
              <button 
                type="button"
                onClick={() => onRemove(nivel, idx)}
                className="ml-1 text-slate-400 hover:text-red-500"
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
      )}

      {aprobadores.length === 0 && (
        <p className="text-xs text-slate-400">
          Sin aprobadores definidos. Cualquier usuario con permisos podrá aprobar.
        </p>
      )}
    </div>
  );
};

const FlujosAprobacionV2 = () => {
  const [modules, setModules] = useState([]);
  const [activeTab, setActiveTab] = useState('GENERAL');
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  
  // Usuarios y grupos para aprobadores
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showMetricsDialog, setShowMetricsDialog] = useState(false);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [showVersionsDialog, setShowVersionsDialog] = useState(false);
  const [showPendingDialog, setShowPendingDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [selectedFlowDetail, setSelectedFlowDetail] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    modulo: '',
    accion: '',  // Nueva: acción específica dentro del módulo
    estado_destino: '',
    niveles_aprobacion: 1,
    requiere_comentario: false,
    requiere_limites: false,
    sla_horas: '',
    activo: true,
    aprobadores: [],          // Nivel 1
    aprobadores_nivel_2: []   // Nivel 2
  });
  
  // Acciones disponibles por módulo
  const [moduleActions, setModuleActions] = useState([]);
  
  // Métricas y logs del flujo seleccionado
  const [flowMetrics, setFlowMetrics] = useState(null);
  const [flowLogs, setFlowLogs] = useState({ logs: [], total: 0 });
  const [flowVersions, setFlowVersions] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [restoringVersion, setRestoringVersion] = useState(false);

  // Cargar módulos
  useEffect(() => {
    loadModules();
    loadDashboardMetrics();
    loadPendingApprovals();
    loadUsersAndGroups();
  }, []);

  // Cargar flujos cuando cambia el tab
  useEffect(() => {
    loadFlows();
  }, [activeTab]);

  const loadModules = async () => {
    try {
      const response = await api.get('/approval-flows/v2/modules');
      setModules(response.data.modules || []);
    } catch (error) {
      console.error('Error loading modules:', error);
    }
  };

  const loadUsersAndGroups = async () => {
    try {
      const [usersRes, groupsRes] = await Promise.all([
        api.get('/users'),
        api.get('/groups/')
      ]);
      setUsers(usersRes.data.filter(u => u.activo));
      setGroups(groupsRes.data || []);
    } catch (error) {
      console.error('Error loading users/groups:', error);
    }
  };

  const loadDashboardMetrics = async () => {
    try {
      const response = await api.get('/approval-flows/v2/dashboard');
      setDashboardMetrics(response.data);
    } catch (error) {
      console.error('Error loading dashboard metrics:', error);
    }
  };

  const loadModuleActions = async (modulo) => {
    if (!modulo || modulo === 'GENERAL') {
      setModuleActions([]);
      return;
    }
    try {
      const response = await api.get(`/approval-flows/v2/modules/${modulo}/actions`);
      setModuleActions(response.data.actions || []);
    } catch (error) {
      console.error('Error loading module actions:', error);
      setModuleActions([]);
    }
  };

  const loadFlows = async () => {
    setLoading(true);
    try {
      const params = activeTab !== 'GENERAL' ? { modulo: activeTab } : {};
      params.incluir_inactivos = true;
      const response = await api.get('/approval-flows/v2/flows', { params });
      setFlows(response.data.flows || []);
    } catch (error) {
      console.error('Error loading flows:', error);
      toast.error('Error al cargar flujos');
    } finally {
      setLoading(false);
    }
  };

  const loadFlowMetrics = async (flowId) => {
    try {
      const response = await api.get(`/approval-flows/v2/flows/${flowId}/metrics`);
      setFlowMetrics(response.data);
    } catch (error) {
      console.error('Error loading flow metrics:', error);
    }
  };

  const loadFlowLogs = async (flowId) => {
    try {
      const response = await api.get('/approval-flows/v2/logs', { params: { flow_id: flowId, limit: 50 } });
      setFlowLogs(response.data);
    } catch (error) {
      console.error('Error loading flow logs:', error);
    }
  };

  const loadFlowVersions = async (flowId) => {
    try {
      const response = await api.get(`/approval-flows/v2/flows/${flowId}/versions`);
      setFlowVersions(response.data.versions || []);
    } catch (error) {
      console.error('Error loading flow versions:', error);
    }
  };

  const loadPendingApprovals = async () => {
    try {
      const response = await api.get('/approval-flows/v2/pending-approvals');
      setPendingApprovals(response.data.pending || []);
    } catch (error) {
      console.error('Error loading pending approvals:', error);
    }
  };

  const handleRestoreVersion = async (flowId, version) => {
    if (!window.confirm(`¿Restaurar a la versión ${version}? Esto revertirá los cambios posteriores.`)) return;
    
    setRestoringVersion(true);
    try {
      await api.post(`/approval-flows/v2/flows/${flowId}/restore/${version}`);
      toast.success(`Flujo restaurado a versión ${version}`);
      setShowVersionsDialog(false);
      loadFlows();
      loadFlowVersions(flowId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar versión');
    } finally {
      setRestoringVersion(false);
    }
  };

  const handleToggleActive = async (flow) => {
    try {
      await api.post(`/approval-flows/v2/flows/${flow.id}/toggle-active`, null, {
        params: { active: !flow.activo }
      });
      toast.success(flow.activo ? 'Flujo desactivado' : 'Flujo activado');
      loadFlows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleToggleProtection = async (flow) => {
    try {
      await api.post(`/approval-flows/v2/flows/${flow.id}/toggle-protection`, null, {
        params: { protected: !flow.protegido }
      });
      toast.success(flow.protegido ? 'Flujo desprotegido' : 'Flujo protegido');
      loadFlows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar protección');
    }
  };

  const handleDeleteFlow = async (flow) => {
    if (!window.confirm('¿Está seguro de eliminar este flujo?')) return;
    
    try {
      await api.delete(`/approval-flows/v2/flows/${flow.id}`);
      toast.success('Flujo eliminado');
      loadFlows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleCreateFlow = async () => {
    try {
      await api.post('/approval-flows/v2/flows', {
        ...formData,
        modulo: activeTab !== 'GENERAL' ? activeTab : formData.modulo,
        sla_horas: formData.sla_horas ? parseInt(formData.sla_horas) : null
      });
      toast.success('Flujo creado correctamente');
      setShowCreateDialog(false);
      resetForm();
      loadFlows();
      loadDashboardMetrics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear flujo');
    }
  };

  const handleUpdateFlow = async () => {
    try {
      await api.put(`/approval-flows/v2/flows/${selectedFlow.id}`, {
        ...formData,
        sla_horas: formData.sla_horas ? parseInt(formData.sla_horas) : null
      });
      toast.success('Flujo actualizado correctamente');
      setShowEditDialog(false);
      resetForm();
      loadFlows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar flujo');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      modulo: '',
      estado_destino: '',
      niveles_aprobacion: 1,
      requiere_comentario: false,
      requiere_limites: false,
      sla_horas: '',
      activo: true,
      aprobadores: [],
      aprobadores_nivel_2: []
    });
    setSelectedFlow(null);
  };

  const openEditDialog = (flow) => {
    setSelectedFlow(flow);
    setFormData({
      nombre: flow.nombre,
      descripcion: flow.descripcion || '',
      modulo: flow.modulo,
      estado_destino: flow.estado_destino,
      niveles_aprobacion: flow.niveles_aprobacion || 1,
      requiere_comentario: flow.requiere_comentario || false,
      requiere_limites: flow.requiere_limites || false,
      sla_horas: flow.sla_horas || '',
      activo: flow.activo,
      aprobadores: flow.aprobadores || [],
      aprobadores_nivel_2: flow.aprobadores_nivel_2 || []
    });
    setShowEditDialog(true);
  };

  // Funciones para agregar/quitar aprobadores
  const addAprobador = (nivel, tipo, valor) => {
    const key = nivel === 1 ? 'aprobadores' : 'aprobadores_nivel_2';
    // Verificar si ya existe
    const exists = formData[key].some(a => a.tipo === tipo && a.valor === valor);
    if (!exists) {
      setFormData(prev => ({
        ...prev,
        [key]: [...prev[key], { tipo, valor }]
      }));
    }
  };

  const removeAprobador = (nivel, index) => {
    const key = nivel === 1 ? 'aprobadores' : 'aprobadores_nivel_2';
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index)
    }));
  };

  const getAprobadorLabel = (aprobador) => {
    if (aprobador.tipo === 'ROL') {
      return `Rol: ${aprobador.valor}`;
    } else if (aprobador.tipo === 'GRUPO') {
      const group = groups.find(g => g.id === aprobador.valor);
      return `Grupo: ${group?.nombre || aprobador.valor}`;
    } else if (aprobador.tipo === 'USUARIO') {
      const user = users.find(u => u.id === aprobador.valor);
      return user?.nombre || aprobador.valor;
    }
    return aprobador.valor;
  };

  const openMetricsDialog = (flow) => {
    setSelectedFlow(flow);
    loadFlowMetrics(flow.id);
    setShowMetricsDialog(true);
  };

  const openLogsDialog = (flow) => {
    setSelectedFlow(flow);
    loadFlowLogs(flow.id);
    setShowLogsDialog(true);
  };

  const openVersionsDialog = (flow) => {
    setSelectedFlow(flow);
    loadFlowVersions(flow.id);
    setShowVersionsDialog(true);
  };

  const openDetailDialog = (flow) => {
    setSelectedFlowDetail(flow);
    setShowDetailDialog(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Componente de tarjeta de flujo
  const FlowCard = ({ flow }) => {
    const ModuloIcon = MODULO_ICONS[flow.modulo] || GitBranch;
    const metrics = flow.metricas || {};
    
    return (
      <Card className={`p-4 ${!flow.activo ? 'opacity-60' : ''}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg bg-slate-100 ${MODULO_COLORS[flow.modulo]}`}>
              <ModuloIcon size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-slate-900">{flow.nombre}</h3>
                {flow.protegido && (
                  <Lock size={14} className="text-amber-600" title="Flujo protegido" />
                )}
                {!flow.activo && (
                  <Badge variant="outline" className="text-xs">Inactivo</Badge>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{flow.descripcion || 'Sin descripción'}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-slate-600">
                <span className="flex items-center gap-1">
                  <Users size={12} />
                  {flow.niveles_aprobacion} nivel{flow.niveles_aprobacion > 1 ? 'es' : ''}
                </span>
                {flow.sla_horas && (
                  <span className="flex items-center gap-1">
                    <Clock size={12} />
                    SLA: {flow.sla_horas}h
                  </span>
                )}
                {flow.requiere_limites && (
                  <span className="flex items-center gap-1 text-green-600">
                    <CreditCard size={12} />
                    Requiere límites
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Métricas mini */}
            <div className="flex items-center gap-3 mr-4 text-xs">
              <div className="text-center">
                <p className="font-semibold text-slate-900">{metrics.total || 0}</p>
                <p className="text-slate-500">Total</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-green-600">{metrics.tasa_aprobacion || 0}%</p>
                <p className="text-slate-500">Aprobación</p>
              </div>
              {metrics.pendientes > 0 && (
                <div className="text-center">
                  <p className="font-semibold text-amber-600">{metrics.pendientes}</p>
                  <p className="text-slate-500">Pendientes</p>
                </div>
              )}
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreVertical size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openDetailDialog(flow)}>
                  <Eye size={14} className="mr-2" />
                  Ver detalle completo
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openMetricsDialog(flow)}>
                  <BarChart3 size={14} className="mr-2" />
                  Ver métricas
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openLogsDialog(flow)}>
                  <History size={14} className="mr-2" />
                  Ver historial
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openVersionsDialog(flow)}>
                  <FileText size={14} className="mr-2" />
                  Ver versiones
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {!flow.protegido && (
                  <>
                    <DropdownMenuItem onClick={() => openEditDialog(flow)}>
                      <Edit2 size={14} className="mr-2" />
                      Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleToggleActive(flow)}>
                      {flow.activo ? <PowerOff size={14} className="mr-2" /> : <Power size={14} className="mr-2" />}
                      {flow.activo ? 'Desactivar' : 'Activar'}
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={() => handleToggleProtection(flow)}>
                  {flow.protegido ? <Unlock size={14} className="mr-2" /> : <Lock size={14} className="mr-2" />}
                  {flow.protegido ? 'Desproteger' : 'Proteger'}
                </DropdownMenuItem>
                {!flow.protegido && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => handleDeleteFlow(flow)}
                      className="text-red-600"
                    >
                      <Trash2 size={14} className="mr-2" />
                      Eliminar
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <GitBranch className="text-blue-600" />
            Flujos de Aprobación
          </h1>
          <p className="text-slate-600 mt-1">Configure flujos de aprobación para cada módulo del sistema</p>
        </div>
        <Button onClick={() => {
          // Si estamos en un tab específico, cargar las acciones de ese módulo
          if (activeTab !== 'GENERAL') {
            setFormData(prev => ({ ...prev, modulo: activeTab }));
            loadModuleActions(activeTab);
          }
          setShowCreateDialog(true);
        }}>
          <Plus size={16} className="mr-2" />
          Nuevo Flujo
        </Button>
      </div>

      {/* Dashboard Cards */}
      {dashboardMetrics && (
        <div className="grid grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <GitBranch className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{dashboardMetrics.total_flujos_activos}</p>
                <p className="text-sm text-slate-500">Flujos Activos</p>
              </div>
            </div>
          </Card>
          <Card 
            className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={() => setShowPendingDialog(true)}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{dashboardMetrics.solicitudes_pendientes}</p>
                <p className="text-sm text-slate-500">Pendientes</p>
              </div>
            </div>
          </Card>
          <Card 
            className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={() => setShowPendingDialog(true)}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{dashboardMetrics.proximas_a_vencer}</p>
                <p className="text-sm text-slate-500">Por Vencer (4h)</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <TrendingUp className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {Object.keys(dashboardMetrics.por_modulo || {}).length}
                </p>
                <p className="text-sm text-slate-500">Módulos con Flujos</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tabs por módulo */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1">
          {modules.map(mod => {
            const Icon = MODULO_ICONS[mod.id] || GitBranch;
            const flowCount = mod.id === 'GENERAL' 
              ? flows.length 
              : flows.filter(f => f.modulo === mod.id).length;
            
            return (
              <TabsTrigger 
                key={mod.id} 
                value={mod.id}
                className="flex items-center gap-2 data-[state=active]:bg-white"
              >
                <Icon size={16} className={MODULO_COLORS[mod.id]} />
                {mod.nombre}
                {flowCount > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {flowCount}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {modules.map(mod => (
          <TabsContent key={mod.id} value={mod.id} className="mt-4">
            {loading ? (
              <div className="text-center py-8 text-slate-500">Cargando flujos...</div>
            ) : (
              <div className="space-y-3">
                {(mod.id === 'GENERAL' ? flows : flows.filter(f => f.modulo === mod.id)).length === 0 ? (
                  <Card className="p-8 text-center">
                    <GitBranch className="mx-auto text-slate-300 mb-3" size={48} />
                    <p className="text-slate-500">No hay flujos configurados para este módulo</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, modulo: mod.id }));
                        setShowCreateDialog(true);
                      }}
                    >
                      <Plus size={16} className="mr-2" />
                      Crear primer flujo
                    </Button>
                  </Card>
                ) : (
                  (mod.id === 'GENERAL' ? flows : flows.filter(f => f.modulo === mod.id)).map(flow => (
                    <FlowCard key={flow.id} flow={flow} />
                  ))
                )}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Dialog Crear Flujo */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuevo Flujo de Aprobación</DialogTitle>
            <DialogDescription>Configure un nuevo flujo de aprobación para el sistema</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.nombre}
                onChange={e => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                placeholder="Ej: Aprobación de Cierre de Casos"
              />
            </div>
            
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={e => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                placeholder="Descripción del flujo..."
                rows={2}
              />
            </div>
            
            {activeTab === 'GENERAL' && (
              <div>
                <Label>Módulo *</Label>
                <Select
                  value={formData.modulo}
                  onValueChange={v => {
                    setFormData(prev => ({ ...prev, modulo: v, accion: '' }));
                    loadModuleActions(v);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar módulo" />
                  </SelectTrigger>
                  <SelectContent>
                    {modules.filter(m => m.id !== 'GENERAL').map(mod => (
                      <SelectItem key={mod.id} value={mod.id}>{mod.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Selector de Acción */}
            <div>
              <Label>Acción / Trigger *</Label>
              <Select
                value={formData.accion}
                onValueChange={v => {
                  const action = moduleActions.find(a => a.id === v);
                  setFormData(prev => ({ 
                    ...prev, 
                    accion: v,
                    estado_destino: action?.id || prev.estado_destino
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="¿Qué acción requiere aprobación?" />
                </SelectTrigger>
                <SelectContent>
                  {moduleActions.length > 0 ? (
                    moduleActions.map(action => (
                      <SelectItem key={action.id} value={action.id}>
                        <div className="flex flex-col">
                          <span>{action.nombre}</span>
                          <span className="text-xs text-slate-500">{action.descripcion}</span>
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="_none" disabled>
                      Seleccione primero un módulo
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Define qué acción específica requiere este flujo de aprobación
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Niveles de Aprobación</Label>
                <Select
                  value={formData.niveles_aprobacion.toString()}
                  onValueChange={v => setFormData(prev => ({ ...prev, niveles_aprobacion: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 nivel</SelectItem>
                    <SelectItem value="2">2 niveles</SelectItem>
                    <SelectItem value="3">3 niveles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>SLA (horas)</Label>
                <Input
                  type="number"
                  value={formData.sla_horas}
                  onChange={e => setFormData(prev => ({ ...prev, sla_horas: e.target.value }))}
                  placeholder="Ej: 24"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.requiere_comentario}
                  onCheckedChange={v => setFormData(prev => ({ ...prev, requiere_comentario: v }))}
                />
                <Label className="cursor-pointer">Requiere comentario</Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.requiere_limites}
                  onCheckedChange={v => setFormData(prev => ({ ...prev, requiere_limites: v }))}
                />
                <Label className="cursor-pointer">Requiere límites</Label>
              </div>
            </div>

            {/* Configuración de Aprobadores - Nivel 1 */}
            <div className="border-t pt-4">
              <Label className="flex items-center gap-2 mb-2">
                <Users size={16} className="text-blue-600" />
                Aprobadores Nivel 1
              </Label>
              <AprobadoresSelectorInline
                nivel={1}
                aprobadores={formData.aprobadores}
                onAdd={addAprobador}
                onRemove={removeAprobador}
                users={users}
                groups={groups}
                getLabel={getAprobadorLabel}
              />
            </div>

            {/* Configuración de Aprobadores - Nivel 2 (si aplica) */}
            {formData.niveles_aprobacion >= 2 && (
              <div className="border-t pt-4">
                <Label className="flex items-center gap-2 mb-2">
                  <Users size={16} className="text-purple-600" />
                  Aprobadores Nivel 2
                </Label>
                <AprobadoresSelectorInline
                  nivel={2}
                  aprobadores={formData.aprobadores_nivel_2}
                  onAdd={addAprobador}
                  onRemove={removeAprobador}
                  users={users}
                  groups={groups}
                  getLabel={getAprobadorLabel}
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateFlow} disabled={!formData.nombre || !formData.estado_destino}>
              Crear Flujo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Editar Flujo */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Flujo</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.nombre}
                onChange={e => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={e => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Niveles de Aprobación</Label>
                <Select
                  value={formData.niveles_aprobacion.toString()}
                  onValueChange={v => setFormData(prev => ({ ...prev, niveles_aprobacion: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 nivel</SelectItem>
                    <SelectItem value="2">2 niveles</SelectItem>
                    <SelectItem value="3">3 niveles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>SLA (horas)</Label>
                <Input
                  type="number"
                  value={formData.sla_horas}
                  onChange={e => setFormData(prev => ({ ...prev, sla_horas: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.requiere_comentario}
                  onCheckedChange={v => setFormData(prev => ({ ...prev, requiere_comentario: v }))}
                />
                <Label className="cursor-pointer">Requiere comentario</Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.requiere_limites}
                  onCheckedChange={v => setFormData(prev => ({ ...prev, requiere_limites: v }))}
                />
                <Label className="cursor-pointer">Requiere límites</Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.activo}
                  onCheckedChange={v => setFormData(prev => ({ ...prev, activo: v }))}
                />
                <Label className="cursor-pointer">Activo</Label>
              </div>
            </div>

            {/* Configuración de Aprobadores - Nivel 1 */}
            <div className="border-t pt-4">
              <Label className="flex items-center gap-2 mb-2">
                <Users size={16} className="text-blue-600" />
                Aprobadores Nivel 1
              </Label>
              <AprobadoresSelectorInline
                nivel={1}
                aprobadores={formData.aprobadores}
                onAdd={addAprobador}
                onRemove={removeAprobador}
                users={users}
                groups={groups}
                getLabel={getAprobadorLabel}
              />
            </div>

            {/* Configuración de Aprobadores - Nivel 2 (si aplica) */}
            {formData.niveles_aprobacion >= 2 && (
              <div className="border-t pt-4">
                <Label className="flex items-center gap-2 mb-2">
                  <Users size={16} className="text-purple-600" />
                  Aprobadores Nivel 2
                </Label>
                <AprobadoresSelectorInline
                  nivel={2}
                  aprobadores={formData.aprobadores_nivel_2}
                  onAdd={addAprobador}
                  onRemove={removeAprobador}
                  users={users}
                  groups={groups}
                  getLabel={getAprobadorLabel}
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancelar</Button>
            <Button onClick={handleUpdateFlow}>Guardar Cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Métricas */}
      <Dialog open={showMetricsDialog} onOpenChange={setShowMetricsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="text-blue-600" size={20} />
              Métricas: {selectedFlow?.nombre}
            </DialogTitle>
          </DialogHeader>
          
          {flowMetrics && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900">{flowMetrics.total}</p>
                  <p className="text-sm text-slate-500">Total Solicitudes</p>
                </Card>
                <Card className="p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{flowMetrics.aprobadas}</p>
                  <p className="text-sm text-slate-500">Aprobadas</p>
                </Card>
                <Card className="p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">{flowMetrics.rechazadas}</p>
                  <p className="text-sm text-slate-500">Rechazadas</p>
                </Card>
                <Card className="p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{flowMetrics.tasa_aprobacion}%</p>
                  <p className="text-sm text-slate-500">Tasa Aprobación</p>
                </Card>
              </div>
              
              {flowMetrics.tiempos && (
                <Card className="p-4">
                  <h4 className="font-medium mb-3">Tiempos de Resolución</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-lg font-semibold text-slate-900">{flowMetrics.tiempos.promedio_horas}h</p>
                      <p className="text-xs text-slate-500">Promedio</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-green-600">{flowMetrics.tiempos.min_horas}h</p>
                      <p className="text-xs text-slate-500">Mínimo</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-amber-600">{flowMetrics.tiempos.max_horas}h</p>
                      <p className="text-xs text-slate-500">Máximo</p>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Logs */}
      <Dialog open={showLogsDialog} onOpenChange={setShowLogsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="text-blue-600" size={20} />
              Historial: {selectedFlow?.nombre}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Acción</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Detalles</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flowLogs.logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-slate-500">
                      No hay registros
                    </TableCell>
                  </TableRow>
                ) : (
                  flowLogs.logs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm">{formatDate(log.timestamp)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{log.action}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{log.user}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {JSON.stringify(log.details).substring(0, 50)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Versiones */}
      <Dialog open={showVersionsDialog} onOpenChange={setShowVersionsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="text-blue-600" size={20} />
              Versiones: {selectedFlow?.nombre}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Versión</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flowVersions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500">
                      No hay versiones anteriores
                    </TableCell>
                  </TableRow>
                ) : (
                  flowVersions.map(ver => (
                    <TableRow key={ver.id}>
                      <TableCell className="font-medium">v{ver.version}</TableCell>
                      <TableCell className="text-sm">{formatDate(ver.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{ver.motivo}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{ver.created_by}</TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          disabled={restoringVersion || selectedFlow?.protegido}
                          onClick={() => handleRestoreVersion(selectedFlow?.id, ver.version)}
                        >
                          <RefreshCw size={14} className={`mr-1 ${restoringVersion ? 'animate-spin' : ''}`} />
                          Restaurar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Aprobaciones Pendientes */}
      <Dialog open={showPendingDialog} onOpenChange={setShowPendingDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="text-amber-600" size={20} />
              Aprobaciones Pendientes
            </DialogTitle>
            <DialogDescription>
              Solicitudes de aprobación pendientes en el sistema
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {pendingApprovals.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <CheckCircle size={48} className="mx-auto mb-3 text-green-400" />
                <p className="font-medium">No hay aprobaciones pendientes</p>
                <p className="text-sm">Todas las solicitudes están al día</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingApprovals.map(approval => {
                  const slaVence = approval.sla_vence_at ? new Date(approval.sla_vence_at) : null;
                  const horasRestantes = slaVence ? Math.max(0, (slaVence - new Date()) / (1000 * 60 * 60)) : null;
                  const isUrgent = horasRestantes !== null && horasRestantes <= 4;
                  const isExpired = horasRestantes !== null && horasRestantes <= 0;
                  
                  return (
                    <Card key={approval.id} className={`p-4 ${isExpired ? 'border-red-300 bg-red-50' : isUrgent ? 'border-amber-300 bg-amber-50' : ''}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-slate-900">{approval.entity_nombre}</h4>
                            <Badge variant="outline" className="text-xs">{approval.modulo}</Badge>
                            <Badge variant="secondary" className="text-xs">Nivel {approval.nivel_actual}/{approval.niveles_totales}</Badge>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">
                            Flujo: {approval.flow_nombre}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            Solicitado: {formatDate(approval.created_at)} por {approval.requested_by}
                          </p>
                        </div>
                        <div className="text-right">
                          {slaVence && (
                            <div className={`text-sm ${isExpired ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-slate-600'}`}>
                              {isExpired ? (
                                <span className="flex items-center gap-1">
                                  <AlertTriangle size={14} />
                                  SLA Vencido
                                </span>
                              ) : (
                                <span className="flex items-center gap-1">
                                  <Clock size={14} />
                                  {horasRestantes.toFixed(1)}h restantes
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPendingDialog(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalle de Flujo */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" style={{ backgroundColor: '#ffffff' }}>
          {selectedFlowDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => {
                    const ModuloIcon = MODULO_ICONS[selectedFlowDetail.modulo] || GitBranch;
                    return <ModuloIcon className={MODULO_COLORS[selectedFlowDetail.modulo]} size={24} />;
                  })()}
                  {selectedFlowDetail.nombre}
                </DialogTitle>
                <DialogDescription>
                  Vista completa del flujo de aprobación
                </DialogDescription>
              </DialogHeader>

              <div className="flex-1 overflow-auto space-y-6 py-4">
                {/* Información General */}
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-4 bg-slate-50">
                    <h4 className="text-sm font-medium text-slate-500 mb-2">Estado</h4>
                    <div className="flex items-center gap-2">
                      {selectedFlowDetail.activo ? (
                        <Badge className="bg-green-100 text-green-700">
                          <Power size={12} className="mr-1" /> Activo
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600">
                          <PowerOff size={12} className="mr-1" /> Inactivo
                        </Badge>
                      )}
                      {selectedFlowDetail.protegido && (
                        <Badge className="bg-amber-100 text-amber-700">
                          <Lock size={12} className="mr-1" /> Protegido
                        </Badge>
                      )}
                    </div>
                  </Card>
                  <Card className="p-4 bg-slate-50">
                    <h4 className="text-sm font-medium text-slate-500 mb-2">Módulo</h4>
                    <p className="font-semibold text-slate-900">{selectedFlowDetail.modulo}</p>
                  </Card>
                </div>

                {/* Descripción */}
                <Card className="p-4 bg-slate-50">
                  <h4 className="text-sm font-medium text-slate-500 mb-2">Descripción</h4>
                  <p className="text-slate-700">{selectedFlowDetail.descripcion || 'Sin descripción'}</p>
                </Card>

                {/* Configuración del Flujo */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Settings size={18} className="text-slate-500" />
                    Configuración del Flujo
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">{selectedFlowDetail.niveles_aprobacion}</div>
                      <div className="text-sm text-slate-500">Niveles de Aprobación</div>
                    </Card>
                    <Card className="p-4 text-center">
                      <div className="text-2xl font-bold text-amber-600">{selectedFlowDetail.sla_horas || '-'}</div>
                      <div className="text-sm text-slate-500">SLA (horas)</div>
                    </Card>
                    <Card className="p-4 text-center">
                      <div className="text-2xl font-bold text-slate-700">{selectedFlowDetail.estado_destino}</div>
                      <div className="text-sm text-slate-500">Estado Destino</div>
                    </Card>
                  </div>
                </div>

                {/* Requisitos */}
                <Card className="p-4">
                  <h4 className="font-medium text-slate-900 mb-3">Requisitos</h4>
                  <div className="flex flex-wrap gap-3">
                    <Badge variant={selectedFlowDetail.requiere_comentario ? "default" : "outline"} className={selectedFlowDetail.requiere_comentario ? "bg-blue-100 text-blue-700" : ""}>
                      {selectedFlowDetail.requiere_comentario ? <CheckCircle size={12} className="mr-1" /> : null}
                      Comentario {selectedFlowDetail.requiere_comentario ? 'Requerido' : 'Opcional'}
                    </Badge>
                    <Badge variant={selectedFlowDetail.requiere_limites ? "default" : "outline"} className={selectedFlowDetail.requiere_limites ? "bg-green-100 text-green-700" : ""}>
                      {selectedFlowDetail.requiere_limites ? <CheckCircle size={12} className="mr-1" /> : null}
                      Límites {selectedFlowDetail.requiere_limites ? 'Requeridos' : 'Opcionales'}
                    </Badge>
                  </div>
                </Card>

                {/* Aprobadores por Nivel */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Users size={18} className="text-slate-500" />
                    Aprobadores Configurados
                  </h3>
                  
                  {/* Nivel 1 */}
                  <Card className="p-4 mb-3">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">1</div>
                      <h4 className="font-medium">Nivel 1</h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(selectedFlowDetail.aprobadores || []).length > 0 ? (
                        selectedFlowDetail.aprobadores.map((a, idx) => (
                          <Badge key={idx} variant="secondary" className="px-3 py-1">
                            {a.tipo === 'ROL' && <Shield size={12} className="mr-1 text-blue-600" />}
                            {a.tipo === 'GRUPO' && <Users size={12} className="mr-1 text-purple-600" />}
                            {a.tipo === 'USUARIO' && <User size={12} className="mr-1 text-green-600" />}
                            {getAprobadorLabel(a)}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-sm text-slate-400">Cualquier usuario con permisos puede aprobar</span>
                      )}
                    </div>
                  </Card>

                  {/* Nivel 2 (si aplica) */}
                  {selectedFlowDetail.niveles_aprobacion >= 2 && (
                    <Card className="p-4 mb-3">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold">2</div>
                        <h4 className="font-medium">Nivel 2</h4>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(selectedFlowDetail.aprobadores_nivel_2 || []).length > 0 ? (
                          selectedFlowDetail.aprobadores_nivel_2.map((a, idx) => (
                            <Badge key={idx} variant="secondary" className="px-3 py-1">
                              {a.tipo === 'ROL' && <Shield size={12} className="mr-1 text-blue-600" />}
                              {a.tipo === 'GRUPO' && <Users size={12} className="mr-1 text-purple-600" />}
                              {a.tipo === 'USUARIO' && <User size={12} className="mr-1 text-green-600" />}
                              {getAprobadorLabel(a)}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-slate-400">Sin configurar</span>
                        )}
                      </div>
                    </Card>
                  )}
                </div>

                {/* Métricas del Flujo */}
                {selectedFlowDetail.metricas && (
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <BarChart3 size={18} className="text-slate-500" />
                      Métricas de Uso
                    </h3>
                    <div className="grid grid-cols-4 gap-3">
                      <Card className="p-3 text-center bg-slate-50">
                        <div className="text-xl font-bold text-slate-900">{selectedFlowDetail.metricas.total || 0}</div>
                        <div className="text-xs text-slate-500">Total Solicitudes</div>
                      </Card>
                      <Card className="p-3 text-center bg-green-50">
                        <div className="text-xl font-bold text-green-600">{selectedFlowDetail.metricas.aprobadas || 0}</div>
                        <div className="text-xs text-slate-500">Aprobadas</div>
                      </Card>
                      <Card className="p-3 text-center bg-red-50">
                        <div className="text-xl font-bold text-red-600">{selectedFlowDetail.metricas.rechazadas || 0}</div>
                        <div className="text-xs text-slate-500">Rechazadas</div>
                      </Card>
                      <Card className="p-3 text-center bg-amber-50">
                        <div className="text-xl font-bold text-amber-600">{selectedFlowDetail.metricas.pendientes || 0}</div>
                        <div className="text-xs text-slate-500">Pendientes</div>
                      </Card>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="border-t pt-4">
                <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                  Cerrar
                </Button>
                {!selectedFlowDetail.protegido && (
                  <Button onClick={() => { setShowDetailDialog(false); openEditDialog(selectedFlowDetail); }}>
                    <Edit2 size={14} className="mr-2" />
                    Editar Flujo
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FlujosAprobacionV2;
